import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Users as UsersIcon, Globe, MoreVertical, X, Edit2, Trash2, Heart, RotateCcw, Archive, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * Gratitude card — styled consistently with the rest of the app.
 * Four subtle tints that stay within the brand palette (not scrapbook).
 */
export const PAPER_STYLES = {
  neutral: {
    bg: "var(--color-surface)",
    border: "var(--color-border-default)",
    accent: "var(--color-brand-primary)",
    dot: "var(--color-brand-primary)",
  },
  violet: {
    bg: "var(--color-brand-ghost)",
    border: "var(--color-border-active)",
    accent: "var(--color-brand-primary)",
    dot: "var(--color-brand-primary)",
  },
  sage: {
    bg: "var(--color-strengths-bg)",
    border: "var(--color-strengths-border)",
    accent: "var(--color-strengths-text)",
    dot: "var(--color-strengths-text)",
  },
  blush: {
    bg: "var(--color-weaknesses-bg)",
    border: "var(--color-weaknesses-border)",
    accent: "var(--color-weaknesses-text)",
    dot: "var(--color-weaknesses-text)",
  },
};

// Backward-compat map (old cream/blush/sage/lavender → new keys)
const LEGACY_MAP = {
  cream: "neutral",
  blush: "blush",
  sage: "sage",
  lavender: "violet",
};

const VISIBILITY_ICONS = {
  private: { icon: Lock, label: "Private" },
  circle: { icon: UsersIcon, label: "Circle" },
  public: { icon: Globe, label: "Public" },
};

// Human-friendly options for the 3-way privacy picker. Order is
// least-exposed → most-exposed so the menu flows naturally.
const VISIBILITY_OPTIONS = [
  { key: "private", label: "Keep to myself", sublabel: "Only you see it", icon: Lock },
  { key: "circle", label: "Just my inner circle", sublabel: "Your connections", icon: UsersIcon },
  { key: "public", label: "Everyone", sublabel: "Anyone who opens your wall", icon: Globe },
];

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffH = (now - d) / 36e5;
  if (diffH < 1) return "just now";
  if (diffH < 24) return `${Math.floor(diffH)}h ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const GratitudeCard = ({
  card,
  onEdit,
  onDelete,
  onHide,
  onVisibility,
  onApprove,   // (card, visibility) => void  — for received 'pending' cards
  onRecover,   // (card) => void  — for received 'removed' cards
  readOnly = false,
  wheelAreaMap = {},
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const styleKey = LEGACY_MAP[card.paper_color] || card.paper_color || "neutral";
  const style = PAPER_STYLES[styleKey] || PAPER_STYLES.neutral;

  const isMine = card.perspective === "mine";
  const isGiven = card.perspective === "given";
  const isReceived = card.perspective === "received";
  const isPendingReceived = isReceived && card.target_visibility === "pending";
  const isRemovedReceived = isReceived && card.target_visibility === "removed";

  // Badge reflects the *viewer's* wall decision:
  // - Mine/Given cards → sender's `visibility` (is it on my own wall?)
  // - Received cards  → `target_visibility` (did I add it to my wall or keep for myself?)
  const badgeKey = isReceived ? card.target_visibility : card.visibility;
  const VisIcon = VISIBILITY_ICONS[badgeKey]?.icon || Lock;
  const visLabel = VISIBILITY_ICONS[badgeKey]?.label || "Private";

  // A card can carry multiple life areas now. We keep `area` for backwards
  // compat with the single-area code path (accent color, first chip) and
  // derive a deduped list `areas` for rendering the header chip strip.
  const _areaKeys = Array.isArray(card.wheel_area_keys) && card.wheel_area_keys.length
    ? card.wheel_area_keys
    : (card.wheel_area_key ? [card.wheel_area_key] : []);
  const areas = _areaKeys
    .map(k => wheelAreaMap[k])
    .filter(Boolean);
  const area = areas[0] || null;
  const accentColor = area?.color || null;
  const tags = Array.isArray(card.tags) ? card.tags : [];

  // Translation overlay — backend attaches `translated: true` when the viewer
  // doesn't speak the card's source language. The user can toggle back to the
  // original via the "Show original" link in the footer.
  const translation = card.translation || null;
  const isTranslated = card.translated && translation && (translation.content || (translation.tags || []).length);
  const { t, i18n } = useTranslation();
  // Render the source language name in the viewer's own language when possible
  // ("from Spanish" in English UI; "del español" in Spanish UI).
  const srcCode = card.source_lang || card.lang;
  let sourceLangName = null;
  try {
    if (srcCode && srcCode !== "und") {
      sourceLangName = new Intl.DisplayNames([i18n.language], { type: "language" }).of(srcCode);
    }
  } catch {
    sourceLangName = srcCode;
  }
  const [showOriginal, setShowOriginal] = useState(false);
  const displayContent = isTranslated && !showOriginal && translation.content != null
    ? translation.content
    : card.content;
  const displayTags = isTranslated && !showOriginal && Array.isArray(translation.tags)
    ? translation.tags
    : tags;

  // Perspective label (footer). For a multi-recipient send (recipients list
  // provided by backend) show "To Ana, Carlos & Sofía" instead of just the
  // first recipient — the same card is shared, just routed to many people.
  let metaLabel = "";
  if (isMine) metaLabel = formatDate(card.created_at);
  else if (isGiven) {
    const recipients = Array.isArray(card.recipients) ? card.recipients : [];
    let toLabel;
    if (recipients.length > 1) {
      const firsts = recipients.map(r => (r?.name || "").split(" ")[0] || "them");
      if (firsts.length === 2) toLabel = `${firsts[0]} & ${firsts[1]}`;
      else if (firsts.length === 3) toLabel = `${firsts[0]}, ${firsts[1]} & ${firsts[2]}`;
      else toLabel = `${firsts.slice(0, 2).join(", ")} +${firsts.length - 2} more`;
    } else {
      toLabel = card.target_name?.split(" ")[0] || "them";
    }
    metaLabel = `To ${toLabel} · ${formatDate(card.created_at)}`;
  } else metaLabel = formatDate(card.created_at);

  const authorFirst = (card.author_name || "").split(" ")[0] || "Someone";

  return (
    <div
      className="relative group transition-all"
      data-testid={`gratitude-card-${card.card_id}`}
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderRadius: 12,
        padding: "16px 18px 14px",
        display: "flex",
        flexDirection: "column",
        borderLeft: accentColor ? `3px solid ${accentColor}` : `1px solid ${style.border}`,
        // Prevent long unbreakable strings (and multi-image grids) from
        // making the card wider than the grid column that hosts it.
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      {/* Top row: left dot(s) + perspective label + menu */}
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          {/* Render one coloured dot per linked life-area (max 5 to keep the
              header tidy). When the card has no area tags, fall back to the
              card's accent or its paper-style dot so we always show *something*. */}
          {areas.length > 0 ? (
            <div className="flex items-center gap-1 flex-shrink-0">
              {areas.slice(0, 5).map((a, i) => (
                <span
                  key={a.key || i}
                  aria-hidden
                  title={a.label}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: a.color,
                    flexShrink: 0,
                  }}
                />
              ))}
            </div>
          ) : (
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: accentColor || style.dot,
                flexShrink: 0,
              }}
            />
          )}
          {!isMine && (
            <span
              className="truncate"
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--color-text-secondary)",
              }}
            >
              {isGiven ? "Given" : "Received"}
            </span>
          )}
          {/* Multi-area chip strip: show the first area's label fully, then
              fold any additional areas into a compact "+N" pill with a
              tooltip listing the rest. Prevents a row of 3 areas blowing
              past the card's right edge on a narrow column. */}
          {areas.length > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full min-w-0"
              style={{
                background: `${areas[0].color}15`,
                color: areas[0].color,
                fontSize: 9,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                border: `0.5px solid ${areas[0].color}30`,
                maxWidth: "100%",
              }}
              title={`Linked to ${areas[0].label}`}
            >
              <span className="truncate">{areas[0].label}</span>
            </span>
          )}
          {areas.length > 1 && (
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full flex-shrink-0"
              style={{
                background: "var(--color-surface-muted, rgba(148,163,184,0.12))",
                color: "var(--color-text-secondary)",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.04em",
                border: "0.5px solid var(--color-border-default, rgba(148,163,184,0.25))",
              }}
              title={areas.slice(1).map(a => a.label).join(" · ")}
              data-testid={`extra-areas-${card.card_id}`}
            >
              {/* Colored dots for the hidden areas, capped at 3 */}
              {areas.slice(1, 4).map((a, idx) => (
                <span
                  key={a.key || idx}
                  aria-hidden
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: a.color,
                    display: "inline-block",
                  }}
                />
              ))}
              <span className="ml-0.5">+{areas.length - 1}</span>
            </span>
          )}
        </div>
        {!readOnly && (isMine || isGiven || isReceived) && (
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
              className="p-1 rounded-md hover:bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`card-menu-${card.card_id}`}
              style={{ color: "var(--color-text-secondary)" }}
              aria-label="Card options"
            >
              {menuOpen ? <X size={13} /> : <MoreVertical size={13} />}
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-1 w-40 rounded-lg shadow-lg overflow-hidden"
                style={{ background: "var(--color-surface)", border: "1px solid var(--color-border-default)", zIndex: 4 }}
                onMouseLeave={() => setMenuOpen(false)}
              >
                {(isMine || isGiven) && onEdit && (
                  <button
                    onClick={() => { setMenuOpen(false); onEdit(card); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50"
                    style={{ color: "var(--color-text-primary)" }}
                    data-testid={`card-edit-${card.card_id}`}
                  >
                    <Edit2 size={13} /> Edit
                  </button>
                )}
                {(isMine || isGiven) && onDelete && (
                  <button
                    onClick={() => { setMenuOpen(false); onDelete(card); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-red-50 text-red-700"
                    data-testid={`card-delete-${card.card_id}`}
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                )}
                {isReceived && !isRemovedReceived && onHide && (
                  <button
                    onClick={() => { setMenuOpen(false); onHide(card); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-red-50 text-red-700"
                    data-testid={`card-hide-${card.card_id}`}
                  >
                    <Trash2 size={13} /> Remove
                  </button>
                )}
                {isRemovedReceived && onRecover && (
                  <button
                    onClick={() => { setMenuOpen(false); onRecover(card); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50"
                    style={{ color: "var(--color-text-primary)" }}
                    data-testid={`card-recover-${card.card_id}`}
                  >
                    <RotateCcw size={13} /> Restore
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pending-received: choice banner at the top of the card */}
      {isPendingReceived && !readOnly && onApprove && (
        <div
          className="mb-3 px-3 py-2.5 rounded-lg"
          style={{
            background: "var(--color-brand-ghost)",
            border: "1px dashed var(--color-border-active)",
          }}
          data-testid={`card-pending-banner-${card.card_id}`}
        >
          <div className="text-xs font-medium flex items-center gap-1.5 mb-2"
            style={{ color: "var(--color-brand-primary)" }}>
            <Heart size={12} />
            Add this to your wall?
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onApprove(card, "public"); }}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-full transition-colors flex-1"
              style={{
                background: "var(--color-brand-primary)",
                color: "var(--color-text-on-brand)",
              }}
              data-testid={`card-approve-public-${card.card_id}`}
            >
              Add to my wall
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onApprove(card, "private"); }}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-full transition-colors"
              style={{
                background: "transparent",
                color: "var(--color-text-primary)",
                border: "1px solid var(--color-border-default)",
              }}
              data-testid={`card-approve-private-${card.card_id}`}
            >
              Keep for myself
            </button>
          </div>
        </div>
      )}

      {/* Removed-received: faint "Removed" ribbon + restore hint */}
      {isRemovedReceived && !readOnly && (
        <div
          className="flex items-center justify-between gap-2 mb-3 px-2.5 py-1.5 rounded-lg"
          style={{
            background: "var(--color-surface-muted, #f8fafc)",
            border: "1px dashed var(--color-border-default)",
          }}
          data-testid={`card-removed-banner-${card.card_id}`}
        >
          <span className="text-[11px] font-medium flex items-center gap-1.5"
            style={{ color: "var(--color-text-secondary)" }}>
            <Archive size={12} /> Removed from your wall
          </span>
          {onRecover && (
            <button
              onClick={(e) => { e.stopPropagation(); onRecover(card); }}
              className="text-[11px] font-semibold hover:underline"
              style={{ color: "var(--color-brand-primary)" }}
              data-testid={`card-inline-recover-${card.card_id}`}
            >
              Restore
            </button>
          )}
        </div>
      )}

      {/* Received-from header: prominent avatar + "From {First}".
          When the author has a public wall (indicated by `author_username`),
          the avatar + name + handle are one clickable region that navigates
          to the author's public profile. */}
      {isReceived && (() => {
        const header = (
          <>
          {card.author_picture ? (
            <img
              src={card.author_picture}
              alt={authorFirst}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                objectFit: "cover",
                flexShrink: 0,
                border: `1px solid ${accentColor || style.border}`,
              }}
            />
          ) : (
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: accentColor ? `${accentColor}20` : "var(--color-brand-ghost)",
                color: accentColor || "var(--color-brand-primary)",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {authorFirst.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0 leading-tight">
            <div
              className="text-[10px] font-semibold uppercase"
              style={{ letterSpacing: "0.1em", color: "var(--color-text-placeholder)" }}
            >
              From
            </div>
            <div
              className="text-sm font-semibold truncate"
              style={{ color: "var(--color-text-primary)" }}
            >
              {authorFirst}
            </div>
            {card.author_username && (
              <div
                className="text-[10px] text-slate-500 truncate"
                data-testid={`card-from-handle-${card.card_id}`}
              >
                @{card.author_username}
              </div>
            )}
          </div>
          </>
        );

        if (card.author_username) {
          return (
            <Link
              to={`/${card.author_username}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2.5 mb-3 pb-3 -mx-1 px-1 rounded-lg hover:bg-black/5 transition-colors"
              style={{ borderBottom: "0.5px dashed var(--color-border-default)" }}
              data-testid={`card-from-${card.card_id}`}
              aria-label={`View ${authorFirst}'s public profile`}
            >
              {header}
            </Link>
          );
        }
        return (
          <div
            className="flex items-center gap-2.5 mb-3 pb-3"
            style={{ borderBottom: "0.5px dashed var(--color-border-default)" }}
            data-testid={`card-from-${card.card_id}`}
          >
            {header}
          </div>
        );
      })()}

      {/* Image(s) (if attached). Supports both the new `images: string[]`
          list and the legacy `image_data_url` single string. 1 image → full
          width; 2+ → compact side-by-side / 2×2 grid. */}
      {(() => {
        const imgs =
          (Array.isArray(card.images) && card.images.length > 0)
            ? card.images
            : (card.image_data_url ? [card.image_data_url] : []);
        if (imgs.length === 0) return null;
        if (imgs.length === 1) {
          return (
            <div
              className="mb-3 -mx-1"
              style={{ borderRadius: 8, overflow: "hidden", border: "1px solid var(--color-border-default)" }}
            >
              <img
                src={imgs[0]}
                alt=""
                loading="lazy"
                style={{ width: "100%", display: "block", maxHeight: 280, objectFit: "cover" }}
                data-testid={`card-image-${card.card_id}`}
              />
            </div>
          );
        }
        return (
          <div
            className="mb-3 -mx-1 grid gap-1"
            style={{
              gridTemplateColumns: imgs.length === 2 ? "1fr 1fr" : "1fr 1fr",
              borderRadius: 8,
              overflow: "hidden",
              border: "1px solid var(--color-border-default)",
            }}
            data-testid={`card-images-${card.card_id}`}
          >
            {imgs.map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt=""
                loading="lazy"
                style={{
                  width: "100%",
                  height: imgs.length === 2 ? 180 : 140,
                  objectFit: "cover",
                  display: "block",
                }}
                data-testid={`card-image-${card.card_id}-${idx}`}
              />
            ))}
          </div>
        );
      })()}

      {/* Body */}
      {displayContent && (
        <p
          className="text-sm"
          style={{
            color: "var(--color-text-primary)",
            lineHeight: 1.55,
            margin: 0,
            flex: (displayTags.length || isTranslated) ? "0 0 auto" : 1,
            whiteSpace: "pre-wrap",
            // Long unbroken sequences (URLs, k/j/l spam) must wrap inside the
            // card rather than extending horizontally across the layout.
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            minWidth: 0,
          }}
        >
          {displayContent}
        </p>
      )}

      {/* Tags — hashtag-style chips under the body */}
      {displayTags.length > 0 && (
        <div
          className="mt-2 flex flex-wrap gap-1"
          style={{ flex: isTranslated ? "0 0 auto" : 1 }}
          data-testid={`card-tags-${card.card_id}`}
        >
          {displayTags.map((t, i) => (
            <span
              key={`${t}-${i}`}
              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium"
              style={{
                background: "rgba(124, 58, 237, 0.08)",
                color: "var(--color-brand-primary, #7c3aed)",
              }}
            >
              #{t}
            </span>
          ))}
        </div>
      )}

      {/* Translation affordance — subtle, italic, toggles to the original text */}
      {isTranslated && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowOriginal(v => !v); }}
          className="mt-2 text-[10px] italic self-start"
          style={{ color: "var(--color-text-placeholder)", flex: 1 }}
          data-testid={`card-translation-toggle-${card.card_id}`}
        >
          {showOriginal
            ? t("gratitude.showingOriginal", { lang: sourceLangName || "" })
            : t("gratitude.translatedFrom", { lang: sourceLangName || "" })}
        </button>
      )}

      {/* Footer */}
      <div
        className="mt-3 flex items-center justify-between gap-2 pt-2"
        style={{ borderTop: "0.5px solid var(--color-border-default)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {isGiven && card.target_picture && (
            <img
              src={card.target_picture}
              alt=""
              style={{
                width: 18, height: 18, borderRadius: "50%",
                objectFit: "cover", flexShrink: 0,
              }}
            />
          )}
          <span
            className="truncate"
            style={{
              fontSize: 11,
              color: "var(--color-text-secondary)",
            }}
          >
            {metaLabel}
          </span>
        </div>
        {!readOnly && isGiven ? null : !readOnly && isMine && onVisibility ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 flex-shrink-0 px-1.5 py-0.5 rounded-full hover:bg-black/5 transition-colors"
                title="Change who can see this"
                data-testid={`visibility-toggle-${card.card_id}`}
                style={{
                  fontSize: 10,
                  color: style.accent,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                <VisIcon size={10} />
                {badgeKey === "private" ? "Private" : visLabel}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="p-1 w-60"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="py-1">
                {VISIBILITY_OPTIONS.map((opt) => {
                  const OptIcon = opt.icon;
                  const active = badgeKey === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!active) onVisibility(card, opt.key);
                        // Close by blurring — the shadcn popover closes on outside click.
                        document.activeElement?.blur?.();
                      }}
                      className="w-full flex items-start gap-2.5 px-2.5 py-2 rounded-md text-left hover:bg-black/5 transition-colors"
                      data-testid={`visibility-option-${opt.key}-${card.card_id}`}
                      aria-pressed={active}
                    >
                      <OptIcon
                        size={16}
                        className="mt-0.5 flex-shrink-0"
                        style={{ color: active ? "var(--color-brand-primary)" : "var(--color-text-secondary)" }}
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-sm font-medium leading-tight"
                          style={{ color: active ? "var(--color-brand-primary)" : "var(--color-text-primary)" }}
                        >
                          {opt.label}
                        </div>
                        <div
                          className="text-[11px] leading-tight mt-0.5"
                          style={{ color: "var(--color-text-secondary)" }}
                        >
                          {opt.sublabel}
                        </div>
                      </div>
                      {active && (
                        <Check size={14} style={{ color: "var(--color-brand-primary)" }} className="flex-shrink-0 mt-1" />
                      )}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        ) : (!readOnly && isReceived && !isPendingReceived && !isRemovedReceived && onApprove ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onApprove(card, badgeKey === "public" ? "private" : "public");
            }}
            className="inline-flex items-center gap-1 flex-shrink-0 px-1.5 py-0.5 rounded-full hover:bg-black/5 transition-colors"
            title={`Click to ${badgeKey === "public" ? "keep for yourself" : "add to your wall"}`}
            data-testid={`target-visibility-toggle-${card.card_id}`}
            style={{
              fontSize: 10,
              color: style.accent,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <VisIcon size={10} />
            {badgeKey === "private" ? "Private" : visLabel}
          </button>
        ) : (!readOnly && (
          <span
            className="inline-flex items-center gap-1 flex-shrink-0"
            title={visLabel}
            style={{
              fontSize: 10,
              color: style.accent,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <VisIcon size={10} />
            {badgeKey === "private" ? "Private" : visLabel}
          </span>
        )))}
      </div>
    </div>
  );
};

export default GratitudeCard;
